<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.resize.submit')"
    :hideSubmitButton="true"
    size="lg"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ title }}</template>

    <template #body>
      <div v-if="slice" class="p-1">
        <SalesStrategyOverrideComponent
          v-model="ignored"
          :overage
          :figuresUnknown
        />

        <p class="text-body-tertiary">{{ body }}</p>

        <label class="form-label" :for="volumeId">{{
          $t("forwards.deals.resize.new_volume")
        }}</label>
        <MwhInputComponent
          :id="volumeId"
          v-model="volume"
          :min="0"
          :invalid="belowFloor"
        />

        <p v-if="slice.escrowed_mwh > 0" class="advisory-note mt-2 mb-0">
          <FontAwesomeIcon icon="fa-regular fa-circle-info" />
          <span>{{
            $t("forwards.deals.resize.escrow_floor", {
              mwh: formatNumberWithSpaces(slice.escrowed_mwh),
            })
          }}</span>
        </p>

        <p v-if="givenUpMwh > 0" class="advisory-note mt-2 mb-0">
          <FontAwesomeIcon icon="fa-regular fa-circle-info" />
          <span>{{
            $t("forwards.deals.resize.shrink_warning", {
              mwh: formatNumberWithSpaces(givenUpMwh),
            })
          }}</span>
        </p>

        <p v-if="errorMessage" class="error-note mt-2 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ $t("forwards.deals.common.cancel") }}
      </button>
      <ActionButtonWithSpinner
        :busy="busy"
        :disabled="!canSubmit"
        :variant="submitVariant"
        @click="submit"
      >
        {{
          beyondStrategy
            ? $t("forwards.deals.resize.submit_beyond_strategy")
            : $t("forwards.deals.resize.submit")
        }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import SalesStrategyOverrideComponent from "@/forwards2/components/SalesStrategyOverrideComponent.vue";
import type {
  CommitmentPoolRow,
  SliceResponse,
} from "@/forwards2/forwardsTypes";
import {
  commitmentPoolOverage,
  hasStrategyFigures,
  useStrategyOverride,
} from "@/forwards2/salesStrategy";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Resize a backed slice.
 *
 * Growing pulls again from the same pool and needs open volume in the same period; shrinking gives
 * the volume back as open and can never go below what is already escrowed. The floor is shown up
 * front, but it is the backend that enforces it — its refusal names the exact figure. Open volume
 * is planned in the plan editor, not here.
 *
 * Only the added volume of a grow is capped by the pool's sales strategy. With the override
 * (`useStrategyOverride`) ticked, the banner and the red button show what the grow takes beyond the
 * strategy, judged against `pool`'s strategy cap for the
 * slice's period. Without that figure the banner says the figures are unknown, and the backend's
 * refusal or its report is the only word on it.
 */
const props = withDefaults(
  defineProps<{
    slice: SliceResponse | null;
    busy?: boolean;
    errorMessage?: string | null;
    /** The slice's pool as the commitment-pool read reports it; null until it has answered. */
    pool?: CommitmentPoolRow | null;
  }>(),
  { busy: false, errorMessage: null, pool: null }
);

const emit = defineEmits<{
  submit: [volumeMwh: number, ignoreSalesStrategy: boolean];
}>();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();

const volumeId = useId();
const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};
const volume = ref<number | null>(null);

const title = computed(() => t("forwards.deals.resize.title"));

const body = computed(() => {
  const slice = props.slice;
  if (slice === null) return "";
  return t("forwards.deals.resize.body", {
    period: slice.period_label,
    pool: slice.pool_name ?? "",
    mwh: formatNumberWithSpaces(slice.volume_mwh),
  });
});

/**
 * What shrinking a commitment hands back — the volume turns open and the pool can sell it to
 * someone else, while the commitment keeps its snapshot for what is left (user decision 2026-09-16).
 */
const givenUpMwh = computed(() => {
  const slice = props.slice;
  if (slice === null || volume.value === null) return 0;
  return Math.max(0, slice.volume_mwh - volume.value);
});

/** The pool's figures for the slice's period are not at hand, so the banner never reads "nothing beyond". */
const figuresUnknown = computed(
  () =>
    props.slice !== null &&
    !hasStrategyFigures(props.pool, props.slice.period_label)
);

const { ignored, overage, beyondStrategy, submitVariant } = useStrategyOverride(
  model,
  () => {
    const slice = props.slice;
    const pool = props.pool;
    if (slice === null || !hasStrategyFigures(pool, slice.period_label))
      return null;
    return commitmentPoolOverage(
      pool,
      slice.period_label,
      (volume.value ?? slice.volume_mwh) - slice.volume_mwh
    );
  }
);

const belowFloor = computed(
  () =>
    props.slice !== null &&
    volume.value !== null &&
    volume.value < props.slice.escrowed_mwh
);

const canSubmit = computed(
  () =>
    !props.busy &&
    volume.value !== null &&
    volume.value >= 0 &&
    volume.value !== props.slice?.volume_mwh
);

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (!isOpen) return;
  volume.value = props.slice?.volume_mwh ?? null;
});

const close = () => {
  model.value = false;
};

const submit = () => {
  if (volume.value !== null) emit("submit", volume.value, ignored.value);
};
</script>
